import {BaseApp, DIALOG_FLAG_DISABLE_BACKDROP_CLICK, DIALOG_FLAG_SHOW_CLOSE} from "@/shared/BaseApp";
import React, {ReactElement} from "react";
import {StyledListItem} from "@/shared/StyledComponents";
import {DW_LG, PAGE_FRAGMENT_WIDTH, PD_LG} from "@/shared/dimens";
import {Box, SvgIcon} from "@mui/material";
import {DevicesOutlined} from "@mui/icons-material";
import {AddDeviceSetupCodeFragment} from "./AddDeviceSetupCodeFragment";
import {Device} from "@/checkin-common/types";
import {CheckinApi} from "@/checkin-common/checkin_api";
import {sleep} from "@/shared/sleep";
import {AppPrefs} from "../app_prefs";
import {DialogFragment, PageFragment, PageFragmentProps} from "@/shared/PageFragment";
import {FormGenContainer, FormGenContainerMode} from "@/shared/FormGenContainer";
import {CheckinPracticeData} from "@/checkin-shared/types";

export class EditDeviceHelper {

  static addDevice(): void {
    const id = BaseApp.CONTEXT.showCustomDialog("Link Device", <CreateDeviceView
      onDeviceActionClicked={(action: CreateDeviceAction) => {
        BaseApp.CONTEXT.hideDialog(id);
        setTimeout(() => {
          switch (action.id) {
            case "setupCode": {
              const id = BaseApp.CONTEXT.showDialog({flags: DIALOG_FLAG_DISABLE_BACKDROP_CLICK | DIALOG_FLAG_SHOW_CLOSE}, () =>
                <AddDeviceSetupCodeFragment finish={(setupCode: string) => {
                  BaseApp.CONTEXT.hideDialog(id);
                  setTimeout(() => {
                    BaseApp.CONTEXT.showProgressDialog("Please wait...", null, async function () {
                      await sleep(1000);
                      const device: Device = await CheckinApi.getInstance().devicesSetup(AppPrefs.getInstance().getSelectedPracticeId(), setupCode);
                      if (device) {
                        BaseApp.CONTEXT.showToast("Device added.");
                      } else {
                        BaseApp.CONTEXT.showToast("Oops, something went wrong.");
                      }
                    });
                  }, 100);
                }}/>
              );
              break;
            }
          }
        }, 100);
      }}
    />);
  }

  static editDevice(device: Device): void {
    BaseApp.CONTEXT.showDialog(
      {flags: DIALOG_FLAG_SHOW_CLOSE | DIALOG_FLAG_DISABLE_BACKDROP_CLICK},
      () => <EditDeviceFragment edit={device} onDeviceSaved={device => BaseApp.CONTEXT.hideDialog()}/>);

  }
}

type CreateDeviceAction = {
  id: string,
  title: string,
  text: string,
  iconType: typeof SvgIcon,
}

const LINK_DEVICE_ACTIONS: CreateDeviceAction[] = [
  {
    id: "setupCode",
    title: "Link a device with setup code",
    text: "Link a device with a setup code to your practice.",
    iconType: DevicesOutlined,
  },
];

function CreateDeviceView({onDeviceActionClicked}: {
  onDeviceActionClicked: (action: CreateDeviceAction) => void
}): ReactElement {
  return <Box
    style={{display: "flex", flexDirection: "column", padding: PD_LG, maxWidth: PAGE_FRAGMENT_WIDTH}}>
    {LINK_DEVICE_ACTIONS.map(action => <StyledListItem
      onClick={() => onDeviceActionClicked(action)}
      title={action.title}
      text={action.text}
      textWrap={true}
      icon={action.iconType}/>)}
  </Box>;
}


type EditDeviceFragmentProps = PageFragmentProps & {
  edit: Device,
  onDeviceSaved: (device: Device) => void,
}

class EditDeviceFragment extends DialogFragment<EditDeviceFragmentProps> {

  private readonly device = this.props.edit;

  protected onCreateState() {
    return {
      ...super.onCreateState(),
      title: "Edit",
    };
  }

  protected styleFlags(): number {
    return PageFragment.STYLE_ACTION_PANEL_TYPE_BUTTON_FLAG;
  }

  protected getActionButtonText(): string {
    return "Save";
  }

  onActionButtonClicked() {
    this.addDevice().then(() => this.props.onDeviceSaved(this.device));
  }

  private async addDevice() {
    await CheckinPracticeData.provider().devices.addListItem(this.device);
  }

  renderContent(): React.ReactElement | null {
    return <Box style={{display: "flex", width: DW_LG}}>
      <FormGenContainer
        mode={FormGenContainerMode.EDIT}
        content={this.device}
        saveActionButtonProvider={this}/>
    </Box>;
  }
}
